import React, { useCallback, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import './OrbGame.css';
import { useSession } from '../../context/SessionContext';
import { useGame } from '../../context/GameContext';
import PlatformHeader from '../../components/PlatformHeader/PlatformHeader';
import Landing from './steps/Landing';
import QuestionInput from './steps/QuestionInput';
import Charging from './steps/Charging';
import Reveal from './steps/Reveal';
import ShareCard from './steps/ShareCard';

export const ROUTES = {
  login: '/login',
  home: '/home',
  question: '/question',
  charge: '/charge',
  prophecy: '/prophecy',
  share: '/share',
};

// Step label shown under the platform header.
const STEP_LABELS = {
  [ROUTES.charge]: 'Orb Charging',
  [ROUTES.share]: 'Download & Share',
};

function SoundIcon({ muted }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 5 6 9H3v6h3l5 4V5Z" fill="currentColor" />
      {muted ? (
        <path d="m16 9 5 6m0-6-5 6" />
      ) : (
        <>
          <path d="M15.5 8.5a5 5 0 0 1 0 7" />
          <path d="M18.5 5.5a9 9 0 0 1 0 13" />
        </>
      )}
    </svg>
  );
}

function CenteredMessage({ children }) {
  return (
    <div className="orb-screen">
      <div className="orb-spacer" />
      {children}
      <div className="orb-spacer" />
    </div>
  );
}

/** Shared chrome for every page; holds pages back until the session is resolved. */
export function GameLayout() {
  const { status, error: sessionError } = useSession();
  const { muted, toggleMute } = useGame();
  const { pathname } = useLocation();

  const stepLabel = status === 'ready' ? STEP_LABELS[pathname] : null;

  // The pad plays throughout, so the mute toggle rides along on every screen.
  const headerRight = status === 'ready' ? (
    <button
      type="button"
      className="orb-icon-btn"
      aria-label={muted ? 'Unmute sounds' : 'Mute sounds'}
      onClick={toggleMute}
    >
      <SoundIcon muted={muted} />
    </button>
  ) : null;

  let content;
  if (status === 'loading') {
    content = <CenteredMessage><div className="orb-loading">Opening the veil…</div></CenteredMessage>;
  } else if (status === 'error') {
    content = (
      <CenteredMessage>
        <h1 className="orb-title">Ask The Orb</h1>
        <div className="orb-error">{sessionError}</div>
      </CenteredMessage>
    );
  } else {
    content = <Outlet />;
  }

  return (
    <div className="orb-app">
      <PlatformHeader right={headerRight} />

      {stepLabel && <div className="orb-step-label">{stepLabel}</div>}

      {content}
    </div>
  );
}

/** The platform launches here with ?otp=…; once the session is ready, the OTP leaves the URL. */
export function LoginPage() {
  return <Navigate to={ROUTES.home} replace />;
}

export function HomePage() {
  const navigate = useNavigate();
  return <Landing onStart={() => navigate(ROUTES.question)} />;
}

export function QuestionPage() {
  const { question, setQuestion } = useGame();
  const navigate = useNavigate();
  return <QuestionInput question={question} onChange={setQuestion} onSubmit={() => navigate(ROUTES.charge)} />;
}

export function ChargePage() {
  const { question, prophecy, tapCount, intensity, registerTap, playTap, consultOrb, resetCharge } = useGame();
  const navigate = useNavigate();

  // Every visit (including browser-back from the prophecy) starts a fresh charge.
  useEffect(() => { resetCharge(); }, [resetCharge]);

  const handleTap = useCallback(() => {
    registerTap();
    playTap(intensity);
  }, [registerTap, playTap, intensity]);

  // Start the request first so /prophecy renders in its loading state, not empty.
  const handleCharged = useCallback(() => {
    consultOrb();
    navigate(ROUTES.prophecy);
  }, [consultOrb, navigate]);

  if (!question.trim()) return <Navigate to={ROUTES.question} replace />;

  return (
    <Charging
      mood={prophecy?.mood || 'default'}
      tapCount={tapCount}
      intensity={intensity}
      onTap={handleTap}
      onCharged={handleCharged}
    />
  );
}

export function ProphecyPage() {
  const { prophecy, loading, askError, resetRound } = useGame();
  const navigate = useNavigate();

  // Nothing to show (e.g. refreshed mid-request): go back and ask again.
  if (!prophecy && !loading && !askError) return <Navigate to={ROUTES.question} replace />;

  return (
    <Reveal
      loading={loading}
      error={askError}
      prophecy={prophecy}
      mood={prophecy?.mood || 'default'}
      onAskAgain={() => { resetRound(); navigate(ROUTES.question); }}
      onShare={() => navigate(ROUTES.share)}
      onRetry={() => navigate(ROUTES.charge)}
    />
  );
}

export function SharePage() {
  const { question, prophecy, reportShare, resetRound } = useGame();
  const navigate = useNavigate();

  if (!prophecy) return <Navigate to={ROUTES.prophecy} replace />;

  return (
    <ShareCard
      question={question}
      prophecy={prophecy}
      onDownloaded={(format) => reportShare('download', format)}
      onShared={(format) => reportShare('share', format)}
      onAskAgain={() => { resetRound(); navigate(ROUTES.question); }}
    />
  );
}
